import { focusRing } from "@minket/ui/lib/styles";
import { cn } from "@minket/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

/**
 * A dimmed action is not something a person can do, so it never keeps the signature ink: it
 * becomes paper with a hairline and muted words, and stays reachable with its reason
 * (`aria-disabled` and `aria-describedby`).
 */
const dimmed =
  "disabled:pointer-events-none disabled:text-muted-foreground aria-disabled:pointer-events-none aria-disabled:text-muted-foreground";
const dimmedFill =
  "disabled:border-border disabled:bg-background aria-disabled:border-border aria-disabled:bg-background";

const buttonVariants = cva(
  [
    "inline-flex max-w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-transparent text-center text-sm font-semibold whitespace-nowrap select-none",
    "transition-[color,background-color,border-color,box-shadow] duration-150 ease-out-quiet",
    focusRing,
    dimmed,
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        // The one action on the page, in signature ink. Hover inks it 12 % towards carbon.
        default: [
          "bg-primary text-primary-foreground hover:bg-[color-mix(in_oklab,var(--primary),var(--foreground)_12%)]",
          dimmedFill,
        ],
        // Removing something: paper in the alarm ink with its edge, never a second fill colour.
        destructive: [
          "border-destructive/60 bg-background text-destructive hover:bg-destructive/8",
          dimmedFill,
        ],
        // Every other action: paper with the control edge, so only one button carries the ink.
        outline:
          "border-input bg-background text-foreground hover:bg-accent disabled:border-border aria-disabled:border-border",
        secondary: ["bg-secondary text-secondary-foreground hover:bg-accent", dimmedFill],
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        // A link is underlined, not only blue: colour is never the only signal (WCAG 1.4.1).
        link: "text-primary underline decoration-1 underline-offset-4 hover:decoration-2",
      },
      size: {
        default: "min-h-9 px-4 py-1.5 has-[>svg]:px-3",
        xs: "min-h-6 gap-1 rounded-sm px-2 py-0.5 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "min-h-8 gap-1.5 px-3 py-1 text-[13px] has-[>svg]:px-2.5",
        lg: "min-h-11 px-5 py-2.5 text-[15px] has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-sm [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    compoundVariants: [{ variant: "link", class: "min-h-0 border-0 px-0 py-0 has-[>svg]:px-0" }],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Render the single child element (a link, for instance) with button styles. */
    asChild?: boolean;
    /**
     * Show a spinner before the label and ignore presses while work is under way. The label
     * stays so the width does not jump. Not available with `asChild`.
     */
    loading?: boolean;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  type,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  const busy = loading && !asChild;

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-busy={busy ? true : undefined}
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }), busy && "pointer-events-none")}
      {...props}
      // A busy button swallows clicks without leaving the tab order.
      onClick={busy ? (event) => event.preventDefault() : props.onClick}
    >
      {asChild ? (
        children
      ) : (
        <>
          {busy ? <BusyMark /> : null}
          {children}
        </>
      )}
    </Comp>
  );
}

/**
 * Work under way: the chain of custody's waiting knot, a dashed ring. It stays still, because
 * Proof has no looping motion; the button is `aria-busy` and keeps its label.
 */
function BusyMark({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      data-slot="busy-mark"
      viewBox="0 0 16 16"
      fill="none"
      className={cn("shrink-0", className)}
    >
      <circle
        cx="8"
        cy="8"
        r="5.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3 2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export type { ButtonProps };
export { BusyMark, Button, buttonVariants };
